/**
 * The gender + birth-year question, in one place because it is asked in three (onboarding, profile
 * settings, and the one-time prompt for members who signed up before the fields existed). Wording that
 * lives in one file cannot drift into three slightly different promises about the same data.
 *
 * Both answers are optional. "ไม่ระบุ" is a button of equal weight, not a greyed-out escape hatch: a
 * consent that cannot be refused is not consent, and a reluctant guess is worse data than a blank.
 */
import type { Gender } from '@shared/types';
import { GENDERS, GENDER_LABEL } from '@shared/types';
import { Field } from './Field';
import { cx } from '../lib/format';

/** 13 is the floor in the Terms; the upper bound of the list is a century back. */
export const MIN_AGE = 13;
export const birthYearRange = () => {
  const now = new Date().getUTCFullYear();
  return { min: now - 100, max: now - MIN_AGE };
};

export interface DemographicsValue { gender: Gender | null; birthYear: number | null }

export function DemographicsFields({ value, onChange, compact }: { value: DemographicsValue; onChange: (v: DemographicsValue) => void; compact?: boolean }) {
  const { min, max } = birthYearRange();
  const years: number[] = [];
  for (let y = max; y >= min; y--) years.push(y);
  const age = value.birthYear ? new Date().getUTCFullYear() - value.birthYear : null;

  return (
    <div class="stack-8">
      <Field label="เพศ" hint={compact ? undefined : 'ใช้ดูภาพรวมของชุมชนเท่านั้น ไม่แสดงบนโปรไฟล์ของคุณ'}>
        <div class="chips" role="group" aria-label="เพศ">
          {GENDERS.map((g) => (
            <button type="button" key={g} class={cx('chip', value.gender === g && 'on')} aria-pressed={value.gender === g ? 'true' : 'false'}
              onClick={() => onChange({ ...value, gender: value.gender === g ? null : g })}>
              {GENDER_LABEL[g]}
            </button>
          ))}
        </div>
      </Field>
      <Field label="ปีเกิด" hint={compact ? undefined : 'ถามแค่ปี ไม่ถามวันเดือน — พอสำหรับดูช่วงอายุของคนเล่น'}>
        <div class="row" style={{ gap: 8, alignItems: 'center' }}>
          <select class="input" value={value.birthYear ?? ''} aria-label="ปีเกิด"
            onChange={(e) => { const v = (e.currentTarget as HTMLSelectElement).value; onChange({ ...value, birthYear: v ? Number(v) : null }); }}>
            <option value="">ไม่ระบุ</option>
            {years.map((y) => <option key={y} value={y}>{y + 543}</option>)}
          </select>
          {age != null && <span class="small muted" style={{ whiteSpace: 'nowrap' }}>อายุ {age} ปี</span>}
        </div>
      </Field>
    </div>
  );
}
